/**
 * Bikram Sambat period helpers for UI navigation.
 * Exact day mapping is resolved by the backend from dbo.NepaliCalendarMonth.
 * (BS 2000–2100 ≈ AD 1943-04-14 to 2044-04-13). No BsAdCalendar table required.
 */

const BS_MONTH_NAMES = [
  'Baishakh',
  'Jestha',
  'Ashadh',
  'Shrawan',
  'Bhadra',
  'Ashwin',
  'Kartik',
  'Mangsir',
  'Poush',
  'Magh',
  'Falgun',
  'Chaitra',
];

const BS_MONTH_LENGTHS = [31, 31, 32, 31, 31, 30, 30, 30, 29, 30, 30, 30];

const approximateBsParts = (date) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const md = month * 100 + day;
  const year = md >= 414 ? date.getFullYear() + 57 : date.getFullYear() + 56;

  if (md >= 414 && md < 515) return { year, month: 1 };
  if (md >= 515 && md < 615) return { year, month: 2 };
  if (md >= 615 && md < 717) return { year, month: 3 };
  if (md >= 717 && md < 817) return { year, month: 4 };
  if (md >= 817 && md < 917) return { year, month: 5 };
  if (md >= 917 && md < 1018) return { year, month: 6 };
  if (md >= 1018 && md < 1117) return { year, month: 7 };
  if (md >= 1117 && md < 1216) return { year, month: 8 };
  if (md >= 1216 || md < 115) return { year, month: 9 };
  if (md >= 115 && md < 213) return { year, month: 10 };
  if (md >= 213 && md < 315) return { year, month: 11 };
  return { year, month: 12 };
};

export const toBsPeriod = (dateLike) => {
  const date = typeof dateLike === 'string' ? new Date(`${dateLike}T00:00:00`) : dateLike;
  const { year, month } = approximateBsParts(date);
  return `${year}-${String(month).padStart(2, '0')}`;
};

export const currentBsPeriod = () => toBsPeriod(new Date());

export const formatBsPeriod = (period) => {
  if (period == null || typeof period !== 'string') return '';
  const match = /^(\d{4})-(\d{2})$/.exec(period.trim());
  if (!match) return period;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const monthName = BS_MONTH_NAMES[month - 1];
  if (!monthName) return `BS ${period}`;
  return `${monthName} ${year} BS`;
};

export const bsMonthNames = () => BS_MONTH_NAMES;

export const buildBsPeriod = (year, month) => {
  const m = Math.max(1, Math.min(12, Math.floor(month)));
  return `${year}-${String(m).padStart(2, '0')}`;
};

export const parseBsPeriod = (period) => {
  if (period == null || typeof period !== 'string') return null;
  const match = /^(\d{4})-(\d{2})$/.exec(period.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || month < 1 || month > 12) return null;
  return { year, month };
};

export const shiftBsPeriod = (period, deltaMonths) => {
  const parsed = parseBsPeriod(period) || parseBsPeriod(currentBsPeriod());
  if (!parsed) return currentBsPeriod();
  const absolute = (parsed.year * 12) + (parsed.month - 1) + deltaMonths;
  const year = Math.floor(absolute / 12);
  const month = (absolute % 12) + 1;
  return buildBsPeriod(year, month);
};

export const bsPeriodToGregorianRange = (period) => {
  const parsed = parseBsPeriod(period);
  if (!parsed) return null;
  const gregorianYear = parsed.year - 57;
  const gregorianMonth = ((parsed.month + 3 - 1) % 12) + 1;
  const start = new Date(gregorianYear, gregorianMonth - 1, 15);
  let endYear = gregorianYear;
  let endMonthIndex = gregorianMonth;
  if (endMonthIndex >= 12) {
    endYear += 1;
    endMonthIndex = 0;
  }
  const end = new Date(endYear, endMonthIndex, 14);
  return { start, end };
};

export const daysInBsMonth = (period) => {
  const parsed = parseBsPeriod(period);
  if (!parsed) return 30;
  return BS_MONTH_LENGTHS[parsed.month - 1] ?? 30;
};

export const formatAdDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
